/**
 * 範例專案：一鍵載入一個「三層功能都看得到東西」的專案。
 *
 * 存在理由是很實際的：`newProject()` 產生的是**空專案**，甘特圖、資源視圖、
 * 成本表、EVM 報表全都是空的，使用者第一次開啟 app 無從得知這些功能長什麼樣。
 * 這個範例刻意包含：
 * - 兩層 WBS + 里程碑（表格的縮排／摺疊、甘特圖的摘要條）
 * - FS 相依鏈（CPM 要徑）
 * - **刻意的過度分配**（Alice 同時 100% 投入兩個並行任務）→ 資源視圖標紅、可撫平
 * - 基準線 + 進度 + 超支 → 成本差異與有意義的 SPI / CPI
 *
 * 沿用 `fixtures`（core 的 builder）與 app 殼層既有做法一致——`project-store.ts`
 * 的初始專案本來就是 `fixtures.makeProject()`。
 */

import {
  applySchedule,
  captureBaseline,
  fixtures,
  minutes,
  money,
  percent,
  rateFromHourly,
  rollUpSummaryProgress,
  saveBaseline,
  schedule,
  units,
  type Assignment,
  type Dependency,
  type Project,
  type Task,
} from '@bigint-project/core';

const RESOURCE_SPECS = [
  { name: 'Alice', group: 'Engineering', hourly: 900 },
  { name: 'Bob', group: 'Engineering', hourly: 800 },
  { name: 'Carol', group: 'Design', hourly: 700 },
  { name: 'Dave', group: 'Design', hourly: 750 },
  { name: 'Erin', group: 'QA', hourly: 600 },
] as const;

const DAY = 480;

export function buildSampleProject(): Project {
  fixtures.resetFixtureCounters();
  const calendar = fixtures.makeCalendar();

  const resources = RESOURCE_SPECS.map((spec, index) =>
    fixtures.makeResource({
      name: spec.name,
      initials: spec.name.slice(0, 2).toUpperCase(),
      group: spec.group,
      maxUnits: units(1),
      standardRate: rateFromHourly(spec.hourly),
      overtimeRate: rateFromHourly(spec.hourly * 1.5),
      costPerUse: money(index === 0 ? 2000 : 0),
    }),
  );
  const [alice, bob, carol, dave, erin] = resources;
  if (!alice || !bob || !carol || !dave || !erin) throw new Error('sample resources missing');

  const phase1 = fixtures.makeSummaryTask({ name: '階段一：設計', outlineLevel: 1, parentId: null });
  const t1 = fixtures.makeTask({
    name: '需求訪談',
    outlineLevel: 2,
    parentId: phase1.id,
    durationMinutes: minutes(DAY * 3),
    fixedCost: money(3000),
  });
  const t2 = fixtures.makeTask({
    name: 'UI 設計',
    outlineLevel: 2,
    parentId: phase1.id,
    durationMinutes: minutes(DAY * 4),
  });
  const t3 = fixtures.makeTask({
    name: 'API 設計',
    outlineLevel: 2,
    parentId: phase1.id,
    durationMinutes: minutes(DAY * 4),
  });

  const phase2 = fixtures.makeSummaryTask({ name: '階段二：開發', outlineLevel: 1, parentId: null });
  const t4 = fixtures.makeTask({
    name: '前端開發',
    outlineLevel: 2,
    parentId: phase2.id,
    durationMinutes: minutes(DAY * 6),
  });
  const t5 = fixtures.makeTask({
    name: '後端開發',
    outlineLevel: 2,
    parentId: phase2.id,
    durationMinutes: minutes(DAY * 6),
  });
  const t6 = fixtures.makeTask({
    name: '整合測試',
    outlineLevel: 2,
    parentId: phase2.id,
    durationMinutes: minutes(DAY * 3),
  });
  const milestone = fixtures.makeMilestone({ name: '正式上線', outlineLevel: 2, parentId: phase2.id });

  const tasks: readonly Task[] = [phase1, t1, t2, t3, phase2, t4, t5, t6, milestone];

  const dependencies: readonly Dependency[] = [
    fixtures.makeDependency(t1.id as string, t2.id as string),
    fixtures.makeDependency(t1.id as string, t3.id as string),
    fixtures.makeDependency(t2.id as string, t4.id as string),
    fixtures.makeDependency(t3.id as string, t5.id as string),
    fixtures.makeDependency(t4.id as string, t6.id as string),
    fixtures.makeDependency(t5.id as string, t6.id as string),
    fixtures.makeDependency(t6.id as string, milestone.id as string),
  ];

  // Alice 同時 100% 投入 t2 與 t3（兩者並行、彼此無相依）→ 刻意製造過度分配。
  const assignments: readonly Assignment[] = [
    fixtures.makeAssignment(t1.id as string, bob.id as string, { work: minutes(DAY * 3), units: units(1) }),
    fixtures.makeAssignment(t2.id as string, alice.id as string, { work: minutes(DAY * 4), units: units(1) }),
    fixtures.makeAssignment(t3.id as string, alice.id as string, { work: minutes(DAY * 4), units: units(1) }),
    fixtures.makeAssignment(t4.id as string, carol.id as string, { work: minutes(DAY * 6), units: units(1) }),
    fixtures.makeAssignment(t4.id as string, dave.id as string, { work: minutes(DAY * 3), units: units(0.5) }),
    fixtures.makeAssignment(t5.id as string, dave.id as string, { work: minutes(DAY * 6), units: units(1) }),
    fixtures.makeAssignment(t6.id as string, erin.id as string, { work: minutes(DAY * 3), units: units(1) }),
  ];

  const draft = fixtures.makeProject({
    name: '範例專案：新版產品開發',
    tasks,
    dependencies,
    resources,
    assignments,
    calendars: [calendar],
    currency: 'TWD',
  });

  const scheduled = applySchedule(draft, schedule({ project: draft }));

  // 基準線在原始排程時間點擷取，之後才加進度／超支，讓 EVM 有真正的差異可看。
  const baseline = captureBaseline(scheduled, {
    number: 0,
    name: 'Baseline 0',
    savedAt: scheduled.startDate,
  });
  const withBaseline = saveBaseline(scheduled, baseline);

  const scheduledT4 = withBaseline.tasks.find((task) => task.id === t4.id);
  const statusDate = scheduledT4?.finish ?? withBaseline.finishDate;
  const completedTaskIds = new Set<string>([t1.id as string, t2.id as string, t3.id as string, t4.id as string]);

  // 進度是在 applySchedule() 之後才蓋上去的，所以那次排程算出的摘要百分比
  // 是「全部 0%」時的結果。這裡收尾時重算一次，摘要才會與子任務一致。
  return rollUpSummaryProgress({
    ...withBaseline,
    statusDate,
    tasks: withBaseline.tasks.map((task) =>
      completedTaskIds.has(task.id as string)
        ? { ...task, percentComplete: percent(100), actualStart: task.start, actualFinish: task.finish }
        : task,
    ),
    assignments: withBaseline.assignments.map((assignment) => {
      if (!completedTaskIds.has(assignment.taskId as string)) return assignment;
      // 前端開發（t4）刻意超支 20%：基準線已用較低的 work 存檔，這裡把實際 work
      // 調高後視為 100% 完成，成本表與 EVM 就會呈現真實的超支差異（CPI < 1）。
      const inflated = assignment.taskId === t4.id;
      const work = inflated ? minutes(Math.round((assignment.work as number) * 1.2)) : assignment.work;
      return { ...assignment, work, actualWork: work, remainingWork: minutes(0) };
    }),
  });
}
